import { PayloadAction } from '@reduxjs/toolkit';
import dotPropImmutable from 'dot-prop-immutable';
import { uniqBy } from 'lodash';
import { SpaceTab } from 'api/space/space-tab';
import { deleteTemplateFromIndexedDb } from 'hooks/use-idb';
import { floatUid } from 'utils/uid';
import { DeleteSpaceTabProjectTemplatePayload } from '../actions';
import {
  ProjectTemplateStore,
  ProjectTemplateSpace,
  ProjectTemplateSpaceTab,
} from '../types';

export const spaceSlice = {
  setupSpacesOnProjectTemplate: (
    state: ProjectTemplateStore,
    {
      payload,
    }: PayloadAction<{ [key: string]: Partial<ProjectTemplateSpace> }>,
  ): ProjectTemplateStore => {
    const result = dotPropImmutable.merge(
      state,
      `data.${state.activeId}.spaceList`,
      payload,
    );
    const spaceListIds = Object.keys(
      dotPropImmutable.get(result, `data.${state.activeId}.spaceList`, {}),
    );

    return dotPropImmutable.set(
      result,
      `data.${state.activeId}.template.templateContent.spaceList`,
      spaceListIds,
    );
  },

  setupSpacesOnProjectTemplateInProgress: (
    state: ProjectTemplateStore,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${state.activeId}.spaceSetup`,
      'IN_PROGRESS',
    );
  },

  setupSpacesOnProjectTemplateComplete: (
    state: ProjectTemplateStore,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${state.activeId}.spaceSetup`,
      'COMPLETE',
    );
  },

  createProjectTemplateSpace: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<Partial<ProjectTemplateSpace>>,
  ): ProjectTemplateStore => {
    const spaceId = floatUid();
    const spaceList = {
      [spaceId]: {
        isNew: true,
        spaceId,
        spaceName: '',
        spaceTabs: [],
        private: payload.private,
      },
    };

    const result = dotPropImmutable.merge(
      state,
      `data.${state.activeId}.spaceList`,
      spaceList,
    );

    const spaceListIds = Object.keys(
      dotPropImmutable.get(result, `data.${state.activeId}.spaceList`, {}),
    );

    return dotPropImmutable.set(
      result,
      `data.${state.activeId}.template.templateContent.spaceList`,
      spaceListIds,
    );
  },

  updateProjectTemplateSpace: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<AtLeast<ProjectTemplateSpace, 'spaceId'>>,
  ): ProjectTemplateStore => {
    const path = `data.${state.activeId}.spaceList.${payload.spaceId}`;
    const result = {
      ...state,
      ...dotPropImmutable.delete(state, `${path}.isNew`),
    };

    return dotPropImmutable.merge(result, path, payload);
  },

  deleteProjectTemplateSpace: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<AtLeast<ProjectTemplateSpace, 'spaceId'>>,
  ): ProjectTemplateStore => {
    const path = `data.${state.activeId}.spaceList.${payload.spaceId}`;
    let result: ProjectTemplateStore = {
      ...state,
      ...dotPropImmutable.delete(state, path),
    };

    const spaceListIds = Object.keys(result.data[state.activeId].spaceList);

    result = dotPropImmutable.set(
      result,
      `data.${state.activeId}.template.templateContent.spaceList`,
      spaceListIds,
    );

    return result;
  },

  deleteProjectTemplateSpaceTab: (
    state: ProjectTemplateStore,
    {
      payload: { spaceTabId, spaceId },
    }: PayloadAction<DeleteSpaceTabProjectTemplatePayload>,
  ): ProjectTemplateStore => {
    // If not specified, default to the active space
    spaceId ??= dotPropImmutable.get(state, 'activeSpaceId', null);

    if (spaceId == null) {
      return state;
    }

    const path = `data.${state.activeId}.spaceList.${spaceId}.spaceTabs`;
    let spaceTabs: SpaceTab[] = dotPropImmutable.get(state, path, []);

    spaceTabs = spaceTabs.filter((tab) => tab.spaceTabId !== spaceTabId);

    deleteTemplateFromIndexedDb([spaceTabId]);

    return dotPropImmutable.set(state, path, spaceTabs);
  },

  setupSpaceTabsOnSpaces: (
    state: ProjectTemplateStore,
    {
      payload: { spaceTab, spaceId },
    }: PayloadAction<{
      spaceTab: AtLeast<
        ProjectTemplateSpaceTab,
        'spaceTabId' | 'spaceTabTitle' | 'spaceTabType'
      >;
      spaceId: number;
    }>,
  ): ProjectTemplateStore => {
    const spaceList: number[] = dotPropImmutable.get(
      state,
      `data.${state.activeId}.template.templateContent.spaceList`,
      [],
    );
    const updateSpaceList = dotPropImmutable.set(
      state,
      `data.${state.activeId}.template.templateContent.spaceList`,
      uniqBy((spaceList ?? []).concat(spaceId), (x) => x.toString()),
    );

    const spaceDetail = dotPropImmutable.get(
      state,
      `data.${state.activeId}.spaceList.${spaceId}`,
      null,
    );

    const updatedSpaceMeta = dotPropImmutable.merge(
      updateSpaceList,
      `data.${state.activeId}.spaceList.${spaceId}`,
      {
        spaceId,
        spaceName: spaceDetail?.spaceName ?? 'Untitled',
      },
    );

    return dotPropImmutable.merge(
      updatedSpaceMeta,
      `data.${state.activeId}.spaceList.${spaceId}.spaceTabs`,
      [spaceTab],
    );
  },

  updateSpaceTabInSpaces: (
    state: ProjectTemplateStore,
    {
      payload: { spaceId, spaceTab },
    }: PayloadAction<{ spaceId: number; spaceTab: ProjectTemplateSpaceTab }>,
  ): ProjectTemplateStore => {
    const path = `data.${state.activeId}.spaceList.${spaceId}.spaceTabs`;
    const spaceTabs = dotPropImmutable.get(state, path, []);

    const updatedSpaceTabs = spaceTabs.map((_spaceTab) => {
      return _spaceTab.spaceTabId === spaceTab.spaceTabId
        ? spaceTab
        : _spaceTab;
    });

    return dotPropImmutable.set(state, path, updatedSpaceTabs);
  },

  setupActiveSpace: (
    state: ProjectTemplateStore,
    { payload: { spaceId } }: PayloadAction<{ spaceId: number | null }>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(state, 'activeSpaceId', spaceId);
  },

  setupActiveSpaceTab: (
    state: ProjectTemplateStore,
    { payload: { spaceTabId } }: PayloadAction<{ spaceTabId: number | null }>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(state, 'activeSpaceTabId', spaceTabId);
  },
};
